import { zodResolver } from "@hookform/resolvers/zod"
import { X } from "lucide-react"
import { Suspense, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import type { OwnerModels } from "@/api/client/owner"
import { ownerApi } from "@/api/client/owner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import { Input } from "@/components/ui/input"
import {
  Pagination,
  PaginationContent,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { Spinner } from "@/components/ui/spinner/Spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

// 検索フォームのスキーマ
const searchFormSchema = z.object({
  ownerId: z.string().optional(),
  ownerName: z.string().optional(),
  ownerNameKana: z.string().optional(),
})

type SearchFormValues = z.infer<typeof searchFormSchema>

// 検索結果の型
export interface OwnerSearchResult {
  id: string
  name: string
  nameKana: string
  echoesOwnerId: string | null
}

// 選択結果の型
export interface SelectedOwner {
  id: string
  name: string
  nameKana: string
  echoesOwnerId: string | null
}

interface OwnerSearchModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (owner: SelectedOwner) => void
}

const ITEMS_PER_PAGE = 10

export function OwnerSearchModal({ open, onOpenChange, onSelect }: OwnerSearchModalProps) {
  const [isSearched, setIsSearched] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchParams, setSearchParams] = useState<SearchFormValues | null>(null)

  const form = useForm<SearchFormValues>({
    resolver: zodResolver(searchFormSchema),
    defaultValues: {
      ownerId: "",
      ownerName: "",
      ownerNameKana: "",
    },
  })

  const handleSearch = (values: SearchFormValues) => {
    setSearchParams(values)
    setCurrentPage(1)
    setIsSearched(true)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleReset = () => {
    form.reset()
    setIsSearched(false)
    setCurrentPage(1)
    setSearchParams(null)
  }

  const handleSelectOwner = (result: OwnerSearchResult) => {
    const selectedData: SelectedOwner = {
      id: result.id,
      name: result.name,
      nameKana: result.nameKana,
      echoesOwnerId: result.echoesOwnerId,
    }
    onSelect(selectedData)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1148px] max-h-[90vh] overflow-y-auto p-0">
        <div className="p-[25px]">
          <DialogHeader className="mb-5">
            <DialogTitle className="text-[18px] font-semibold text-[#3f4652]">
              オーナー検索
            </DialogTitle>
          </DialogHeader>

          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute right-[14px] top-[14px] rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100"
          >
            <X className="h-[18px] w-[18px] text-[#354267]" />
            <span className="sr-only">閉じる</span>
          </button>

          <div className="space-y-[30px]">
            <div className="border border-slate-200 rounded-[10px]">
              <form
                onSubmit={form.handleSubmit(handleSearch)}
                className="py-[25px] w-[434px] mx-auto"
              >
                <div className="space-y-[15px]">
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="ownerId"
                      className="text-xs font-semibold text-[#3f4652] w-[84px]"
                    >
                      オーナーID
                    </label>
                    <div className="w-[350px]">
                      <Input id="ownerId" {...form.register("ownerId")} />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="ownerName"
                      className="text-xs font-semibold text-[#3f4652] w-[84px]"
                    >
                      オーナー名
                    </label>
                    <div className="w-[350px]">
                      <Input id="ownerName" {...form.register("ownerName")} />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="ownerNameKana"
                      className="text-xs font-semibold text-[#3f4652] w-[84px]"
                    >
                      オーナー名(カナ)
                    </label>
                    <div className="w-[350px]">
                      <Input id="ownerNameKana" {...form.register("ownerNameKana")} />
                    </div>
                  </div>
                </div>

                <div className="flex justify-center gap-5 mt-5">
                  <Button type="submit" variant="default">
                    検索する
                  </Button>
                  <Button type="button" variant="outline" onClick={handleReset}>
                    検索をリセット
                  </Button>
                </div>
              </form>
            </div>

            {isSearched && (
              <ReactErrorBoundary>
                <Suspense fallback={<OwnerSearchModalFallback />}>
                  <OwnerSearchModalContent
                    searchParams={searchParams}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    onSelectOwner={handleSelectOwner}
                  />
                </Suspense>
              </ReactErrorBoundary>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface OwnerSearchModalContentProps {
  searchParams: SearchFormValues | null
  currentPage: number
  onPageChange: (page: number) => void
  onSelectOwner: (result: OwnerSearchResult) => void
}

function OwnerSearchModalContent({
  searchParams,
  currentPage,
  onPageChange,
  onSelectOwner,
}: OwnerSearchModalContentProps) {
  const apiParams = useMemo(() => {
    const params: OwnerModels.OwnerListParams = {
      page: currentPage,
      limit: ITEMS_PER_PAGE,
    }

    if (searchParams?.ownerId) {
      params.id = searchParams.ownerId
    }
    if (searchParams?.ownerName) {
      params.nameLike = searchParams.ownerName
    }
    if (searchParams?.ownerNameKana) {
      params.nameKanaLike = searchParams.ownerNameKana
    }

    return params
  }, [searchParams, currentPage])

  const { data } = ownerApi.useOwnerListSuspense(apiParams)

  // APIレスポンスをOwnerSearchResultに変換
  const searchResults: OwnerSearchResult[] = useMemo(() => {
    return data.owners.map((owner) => ({
      id: owner.id,
      name: `${owner.lastName} ${owner.firstName}`,
      nameKana: `${owner.lastNameKana} ${owner.firstNameKana}`,
      echoesOwnerId: owner.echoesOwnerId,
    }))
  }, [data.owners])

  const totalCount = data.total
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE)

  // ページ番号の配列を生成
  const getPageNumbers = () => {
    const pages: number[] = []
    const maxVisiblePages = 5
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }
    return pages
  }

  return (
    <div className="space-y-4">
      <div className="rounded-[10px] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-white border border-slate-200">
              <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                オーナーID
              </TableHead>
              <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                オーナー名
              </TableHead>
              <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                オーナー名(カナ)
              </TableHead>
              <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                案件へ適用
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {searchResults.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-slate-400">
                  検索結果がありません
                </TableCell>
              </TableRow>
            ) : (
              searchResults.map((result) => (
                <TableRow key={result.id} className="border border-slate-200">
                  <TableCell className="text-xs font-medium text-[#3f4652] py-2 px-4">
                    {result.id}
                  </TableCell>
                  <TableCell className="text-xs font-medium text-[#3f4652] py-2 px-4">
                    {result.name}
                  </TableCell>
                  <TableCell className="text-xs font-medium text-[#3f4652] py-2 px-4">
                    {result.nameKana}
                  </TableCell>
                  <TableCell className="py-2 px-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={Boolean(result.echoesOwnerId)}
                      title={
                        result.echoesOwnerId
                          ? "Echoes連携オーナーは物件追加に選択できません"
                          : undefined
                      }
                      onClick={() => onSelectOwner(result)}
                      className="text-xs font-bold"
                    >
                      この内容で適用
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">
            {totalCount}件中 {(currentPage - 1) * ITEMS_PER_PAGE + 1}-
            {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)}
            件を表示
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationFirst onClick={() => onPageChange(1)} disabled={currentPage === 1} />
              </PaginationItem>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                />
              </PaginationItem>
              {getPageNumbers().map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    onClick={() => onPageChange(page)}
                    isActive={page === currentPage}
                    className="h-8 w-8 cursor-pointer"
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLast
                  onClick={() => onPageChange(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  )
}

function OwnerSearchModalFallback() {
  return (
    <div className="p-10 flex flex-col items-center justify-center">
      <Spinner className={"text-slate-600 size-10 transition-opacity"} />
    </div>
  )
}
